import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { renderIcon } from '../FlowchartView/utils/renderIcon';
import OpenInViewDropdown from '../shared/OpenInViewDropdown';

const SearchIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.35-4.35"/>
  </svg>
);

const MissionSearchModal = ({
  isOpen,
  onClose,
  tasks = [],
  statuses = [],
  campaignId,
  currentView = 'flow',
  customViews = [],
  activeCustomViewId = null,
  onGoToMission,
  allSprints = [],
  showNestedItems = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Get status info for a task
  const getStatusInfo = useCallback((task) => {
    const status = statuses.find(s => s.id === task.status);
    return {
      name: status?.name || 'Unknown',
      color: status?.color || '#6b7280',
    };
  }, [statuses]);

  // Build a map of task ID to its source campaign ID (for nested items)
  const taskCampaignMap = useMemo(() => {
    const map = new Map();
    
    // All top-level tasks belong to the current campaign
    tasks.forEach(task => {
      map.set(task.id, campaignId);
    });
    
    // For nested items, map them to their actual campaign
    if (currentView === 'todolist' && showNestedItems) {
      const visitedSprintIds = new Set();
      
      const collectNestedTaskCampaigns = (sprintTasks) => {
        for (const task of sprintTasks) {
          if (task.nestedSprintId && !visitedSprintIds.has(task.nestedSprintId)) {
            visitedSprintIds.add(task.nestedSprintId);
            const nestedSprint = allSprints.find(s => s.id === task.nestedSprintId);
            if (nestedSprint?.tasks) {
              nestedSprint.tasks.forEach(nestedTask => {
                map.set(nestedTask.id, nestedSprint.id);
              });
              collectNestedTaskCampaigns(nestedSprint.tasks);
            }
          }
        }
      };
      
      collectNestedTaskCampaigns(tasks);
    }
    
    return map;
  }, [tasks, campaignId, currentView, showNestedItems, allSprints]);

  // Build searchable tasks list
  // In todo view with showNestedItems enabled, include tasks from nested campaigns
  const searchableTasks = useMemo(() => {
    // Only include nested tasks in todolist view when showNestedItems is on
    if (currentView !== 'todolist' || !showNestedItems) {
      return tasks;
    }
    
    // Collect all tasks including from nested sprints (same logic as useTodoListData)
    const allTasks = [...tasks];
    const visitedSprintIds = new Set();
    
    const collectNestedTasks = (sprintTasks) => {
      for (const task of sprintTasks) {
        if (task.nestedSprintId && !visitedSprintIds.has(task.nestedSprintId)) {
          visitedSprintIds.add(task.nestedSprintId);
          const nestedSprint = allSprints.find(s => s.id === task.nestedSprintId);
          if (nestedSprint?.tasks) {
            allTasks.push(...nestedSprint.tasks);
            collectNestedTasks(nestedSprint.tasks);
          }
        }
      }
    };
    
    collectNestedTasks(tasks);
    return allTasks;
  }, [tasks, currentView, showNestedItems, allSprints]);

  // Filter tasks based on search query (frontend filtering)
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return searchableTasks.filter(task => 
      (task.name || '').toLowerCase().includes(query)
    ).slice(0, 50); // Limit to 50 results
  }, [searchableTasks, searchQuery]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Close on escape or backdrop click
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleResultClick = useCallback((task) => {
    onGoToMission?.(task.id, currentView);
    onClose?.();
    setSearchQuery('');
  }, [currentView, onGoToMission, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="mission-search-modal">
      <div className="mission-search-modal__backdrop" onClick={onClose} />
      <div className="mission-search-modal__panel" ref={containerRef}>
        <div className="mission-search-modal__header">
          <div className="mission-search-modal__search-box">
            <SearchIcon size={15} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search missions in this campaign..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="mission-search-modal__clear"
                onClick={() => {
                  setSearchQuery('');
                  inputRef.current?.focus();
                }}
              >
                ✕
              </button>
            )}
          </div>
          <button className="mission-search-modal__close" onClick={onClose} title="Close (Esc)">✕</button>
        </div>

        <div className="mission-search-modal__body">
          {searchQuery.trim() && searchResults.length === 0 && (
            <div className="mission-search-modal__empty">No matches</div>
          )}
          {!searchQuery.trim() && (
            <div className="mission-search-modal__hint">Type to search missions in this campaign</div>
          )}

          {searchResults.length > 0 && (
            <div className="mission-search-modal__results">
              {searchResults.map(task => {
                const statusInfo = getStatusInfo(task);
                const taskCampaignId = taskCampaignMap.get(task.id) || campaignId;
                const isFromNestedCampaign = taskCampaignId !== campaignId;
                
                return (
                  <div
                    key={task.id}
                    className="mission-search-modal__item"
                    style={{ '--status-color': statusInfo.color }}
                    onClick={() => handleResultClick(task)}
                  >
                    <span
                      className="mission-search-modal__status-dot"
                      style={{ backgroundColor: statusInfo.color }}
                      title={statusInfo.name}
                    />
                    <span className="mission-search-modal__icon">
                      {renderIcon(task.icon, {
                        size: '15px',
                        fallback: task.nodeType === 'decision' ? '◇' : '🎯'
                      })}
                    </span>
                    <div className="mission-search-modal__item-content">
                      <span className="mission-search-modal__item-name">{task.name}</span>
                      <span className="mission-search-modal__item-status">{statusInfo.name}</span>
                    </div>
                    <div className="mission-search-modal__actions">
                      <OpenInViewDropdown
                        taskId={task.id}
                        campaignId={campaignId}
                        sourceCampaignId={isFromNestedCampaign ? taskCampaignId : null}
                        currentView={currentView}
                        customViews={customViews}
                        activeCustomViewId={activeCustomViewId}
                        variant="button"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MissionSearchModal;
